/**
 * Utilitaires généraux pour BTPal
 */

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// ========================================
// Classe CSS utilities
// ========================================

/**
 * Merge Tailwind classes intelligemment
 */
export function cn(...inputs: ClassValue[]): string {
    return twMerge(clsx(inputs));
}

// ========================================
// Formatage
// ========================================

/**
 * Formate un montant en euros
 */
export function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
}

/**
 * Formate une date en français
 */
export function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(date);
}

/**
 * Formate une date courte (JJ/MM/AAAA)
 */
export function formatDateShort(dateString: string): string {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);
}

/**
 * Formate une date relative (dans X jours, il y a X jours)
 */
export function formatRelativeDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return 'Demain';
    if (diffDays === -1) return 'Hier';
    if (diffDays > 0) return `Dans ${diffDays} jours`;
    return `Il y a ${Math.abs(diffDays)} jours`;
}

/**
 * Convertit une date en format ISO pour les inputs
 */
export function toInputDate(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toISOString().split('T')[0];
}

// ========================================
// Calculs
// ========================================

/**
 * Calcule le montant HT total des lignes de facture
 */
export function calculateTotalHt(lines: Array<{ quantity: number; unitPriceHt: number }>): number {
    return lines.reduce((total, line) => total + line.quantity * line.unitPriceHt, 0);
}

/**
 * Calcule le montant de TVA pour une ligne
 */
export function calculateLineVat(line: { quantity: number; unitPriceHt: number; vatRate: number }): number {
    const ht = line.quantity * line.unitPriceHt;
    return ht * (line.vatRate / 100);
}

/**
 * Calcule le montant TTC pour une ligne
 */
export function calculateLineTtc(line: { quantity: number; unitPriceHt: number; vatRate: number }): number {
    const ht = line.quantity * line.unitPriceHt;
    return ht + calculateLineVat(line);
}

// ========================================
// Validation
// ========================================

/**
 * Vérifie si un email est valide
 */
export function isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Vérifie si un SIRET est valide (14 chiffres)
 */
export function isValidSiret(siret: string): boolean {
    const cleanSiret = siret.replace(/\s/g, '');
    return /^\d{14}$/.test(cleanSiret);
}

// ========================================
// Labels / Traductions
// ========================================

export const invoiceStatusLabels: Record<string, string> = {
    DRAFT: 'Brouillon',
    EMITTED: 'Émise',
    PAID: 'Payée',
    OVERDUE: 'En retard',
    CANCELLED: 'Annulée',
};

export const expenseCategoryLabels: Record<string, string> = {
    SALARIES: 'Salaires',
    MATERIALS: 'Matériaux',
    RENT: 'Loyer',
    INSURANCE: 'Assurance',
    TOOLS: 'Outillage',
    SUBCONTRACTING: 'Sous-traitance',
    TRANSPORT: 'Transport',
    FUEL: 'Carburant',
    VEHICLE_RENTAL: 'Location véhicule',
    TRAINING: 'Formation',
    TAXES: 'Taxes / cotisations',
    OTHER: 'Autre',
};

export const expenseFrequencyLabels: Record<string, string> = {
    MONTHLY: 'Mensuelle',
    QUARTERLY: 'Trimestrielle',
    YEARLY: 'Annuelle',
};

export const expenseStatusLabels: Record<string, string> = {
    PLANNED: 'Planifiée',
    COMMITTED: 'Engagée',
    PAID: 'Payée',
    CANCELLED: 'Annulée',
};

export const quoteStatusLabels: Record<string, string> = {
    DRAFT: 'Brouillon',
    SENT: 'Finalisé',
    ACCEPTED: 'Accepté',
    REFUSED: 'Refusé',
    EXPIRED: 'Expiré',
};

export const projectStatusLabels: Record<string, string> = {
    DRAFT: 'Brouillon',
    CONFIRMED: 'Confirmé',
    IN_PROGRESS: 'En cours',
    COMPLETED: 'Terminé',
    SETTLED: 'Soldé',
    CANCELLED: 'Annulé',
};

export const activityLabels: Record<string, string> = {
    electricien: 'Électricien',
    plombier: 'Plombier',
    macon: 'Maçon',
    couvreur: 'Couvreur',
    carreleur: 'Carreleur',
    menuisier: 'Menuisier',
    peintre: 'Peintre',
    chauffagiste: 'Chauffagiste',
    autre: 'Autre',
};

// ========================================
// Couleurs des statuts
// ========================================

export function getStatusColor(status: string): string {
    const colors: Record<string, string> = {
        DRAFT: 'bg-blue-500',
        EMITTED: 'bg-green-500',
        PAID: 'bg-purple-500',
        OVERDUE: 'bg-red-500',
        CANCELLED: 'bg-gray-500',
    };
    return colors[status] || 'bg-gray-500';
}

export function getCategoryColor(category: string): string {
    const colors: Record<string, string> = {
        SALARIES: 'bg-red-100 text-red-800',
        MATERIALS: 'bg-purple-100 text-purple-800',
        RENT: 'bg-blue-100 text-blue-800',
        INSURANCE: 'bg-green-100 text-green-800',
        TOOLS: 'bg-yellow-100 text-yellow-800',
        SUBCONTRACTING: 'bg-orange-100 text-orange-800',
        TRANSPORT: 'bg-cyan-100 text-cyan-800',
        FUEL: 'bg-amber-100 text-amber-800',
        VEHICLE_RENTAL: 'bg-teal-100 text-teal-800',
        TRAINING: 'bg-indigo-100 text-indigo-800',
        TAXES: 'bg-rose-100 text-rose-800',
        OTHER: 'bg-gray-100 text-gray-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
}

export function getQuoteStatusColor(status: string): string {
    const colors: Record<string, string> = {
        DRAFT: 'bg-gray-100 text-gray-800',
        SENT: 'bg-blue-100 text-blue-800',
        ACCEPTED: 'bg-green-100 text-green-800',
        REFUSED: 'bg-red-100 text-red-800',
        EXPIRED: 'bg-yellow-100 text-yellow-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
}

export function getProjectStatusColor(status: string): string {
    const colors: Record<string, string> = {
        DRAFT: 'bg-gray-100 text-gray-800',
        CONFIRMED: 'bg-blue-100 text-blue-800',
        IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
        COMPLETED: 'bg-green-100 text-green-800',
        SETTLED: 'bg-purple-100 text-purple-800',
        CANCELLED: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
}

export function getExpenseStatusColor(status: string): string {
    const colors: Record<string, string> = {
        PLANNED: 'bg-blue-100 text-blue-800',
        COMMITTED: 'bg-yellow-100 text-yellow-800',
        PAID: 'bg-green-100 text-green-800',
        CANCELLED: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
}
